import { useState, useMemo, useEffect } from 'react'
import { Control, useController, FieldValues, Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
    Autocomplete,
    AutocompleteItem,
    AutocompleteProps,
    Tooltip
} from '@/components/nextui'
import { OptionsField } from '@/types/forms/optionsField'

interface SelectionHandlerDeps {
    options: OptionsField[]
    valueKey: string
    labelKey: string
    field: { value: any; onChange: (v: any) => void }
    onBeforeChange?: (v: string) => Promise<boolean>
    onChangeCustom?: (v: string) => void
    setValueExternal?: (v: string) => void
    setInputValue: (v: string) => void
    setPendingValue: (v: string | null) => void
    setIsValidating: (v: boolean) => void
    restorePreviousValue: (previousValue: any) => void
}

const handleSelectionChangeAsync = async (
    value: React.Key | null,
    deps: SelectionHandlerDeps
): Promise<void> => {
    const {
        options,
        valueKey,
        labelKey,
        field,
        onBeforeChange,
        onChangeCustom,
        setValueExternal,
        setInputValue,
        setPendingValue,
        setIsValidating,
        restorePreviousValue
    } = deps

    const notifyEmpty = (): void => {
        if (onChangeCustom) {
            onChangeCustom('')
        }

        if (setValueExternal) {
            setValueExternal('')
        }
    }

    if (!value) {
        setInputValue('')
        field.onChange('')
        setPendingValue(null)
        notifyEmpty()

        return
    }

    const selected = options.find(
        (opt) => String((opt as any)[valueKey]) === String(value)
    )

    if (!selected) {
        setInputValue('')
        field.onChange('')
        setPendingValue(null)
        notifyEmpty()

        return
    }

    setPendingValue(value as string)
    setInputValue((selected as any)[labelKey])

    if (onBeforeChange) {
        setIsValidating(true)

        try {
            const isValid = await onBeforeChange(value as string)

            if (!isValid) {
                setPendingValue(null)
                setInputValue('')
                restorePreviousValue(field.value)
                setIsValidating(false)

                return
            }
        } catch (err) {
            console.error('Error en validación previa:', err)
            setPendingValue(null)
            setInputValue('')
            setIsValidating(false)

            return
        }

        setIsValidating(false)
    }

    setPendingValue(null)
    field.onChange(value)

    if (onChangeCustom) {
        onChangeCustom(value as string)
    }

    if (setValueExternal) {
        setValueExternal(value as string)
    }
}

interface AutocompleteControlProps<T extends FieldValues>
    extends Omit<AutocompleteProps, 'onChange' | 'children'> {
    name: Path<T>
    control: Control<T>
    rules?: Record<string, any>
    options: OptionsField[]
    label?: string
    placeholder?: string
    labelKey?: string
    valueKey?: string
    onChangeCustom?: (value: string) => void
    onBeforeChange?: (value: string) => Promise<boolean>
    customRender?: (option: any) => React.ReactNode
    setValue?: (value: string) => void
    listboxProps?: any
    showScrollIndicators?: boolean
    scrollShadowProps?: any
    onInputChange?: (value: string) => void
    disableLocalFilter?: boolean // New prop to disable local filtering
    disabledTooltip?: string
}

const useAutocompleteLogic = <T extends FieldValues>({
    name,
    control,
    rules,
    options,
    valueKey = 'value',
    labelKey = 'label',
    onInputChange,
    onBeforeChange,
    onChangeCustom,
    setValue,
    disableLocalFilter = false
}: AutocompleteControlProps<T>) => {
    const [inputValue, setInputValue] = useState<string>('')
    const [isFocused, setIsFocused] = useState(false)
    const [isValidating, setIsValidating] = useState(false)
    const [pendingValue, setPendingValue] = useState<string | null>(null)

    const {
        field,
        fieldState: { error }
    } = useController({
        name,
        control,
        rules
    })

    const currentInputValue = useMemo(() => {
        if (!inputValue && field.value) {
            const selectedItem = options.find(
                (opt) => String((opt as any)[valueKey]) === String(field.value)
            )

            if (selectedItem) {
                return (selectedItem as any)[labelKey]
            }
        }

        return inputValue
    }, [inputValue, field.value, options, valueKey, labelKey])

    const filteredItems = useMemo(() => {
        // If local filtering is disabled, return all options (used for server-side search)
        if (disableLocalFilter) {
            return options
        }

        return inputValue
            ? options.filter((option) => {
                  const optionLabel = String(
                      (option as any)[labelKey]
                  ).toLowerCase()
                  const searchValue = inputValue.toLowerCase()

                  return optionLabel.includes(searchValue)
              })
            : options
    }, [inputValue, options, labelKey, disableLocalFilter])

    const handleInputChange = (value: string) => {
        setInputValue(value)
        if (onInputChange) {
            onInputChange(value)
        }
        if (field.value) {
            const selectedItem = options.find(
                (opt) => String((opt as any)[valueKey]) === String(field.value)
            )

            if (selectedItem && (selectedItem as any)[labelKey] !== value) {
                field.onChange('')
            }
        }
    }

    const restorePreviousValue = (previousValue: any) => {
        if (!previousValue) {
            return
        }
        const prevSelected = options.find(
            (opt) => String((opt as any)[valueKey]) === String(previousValue)
        )

        if (prevSelected) {
            setInputValue((prevSelected as any)[labelKey])
        }
    }

    const handleSelectionChange = async (
        value: React.Key | null
    ): Promise<void> => {
        await handleSelectionChangeAsync(value, {
            options,
            valueKey,
            labelKey,
            field,
            onBeforeChange,
            onChangeCustom,
            setValueExternal: setValue,
            setInputValue,
            setPendingValue,
            setIsValidating,
            restorePreviousValue
        })
    }

    // Sincronizar el texto visible cuando el valor del campo cambia externamente
    useEffect(() => {
        if (!isFocused) {
            if (!field.value) {
                setInputValue('')
            } else {
                const selectedItem = options.find(
                    (opt) =>
                        String((opt as any)[valueKey]) === String(field.value)
                )

                if (selectedItem) {
                    setInputValue((selectedItem as any)[labelKey])
                }
            }
        }
    }, [field.value, isFocused, options, valueKey, labelKey])

    return {
        field,
        error,
        inputValue,
        currentInputValue,
        filteredItems,
        pendingValue,
        isValidating,
        handleInputChange,
        handleSelectionChange,
        setIsFocused
    }
}

// eslint-disable-next-line complexity
export function AutocompleteControl<T extends FieldValues>(
    props: AutocompleteControlProps<T>
): JSX.Element {
    const {
        label,
        placeholder,
        customRender,
        listboxProps,
        showScrollIndicators,
        scrollShadowProps,
        valueKey = 'value',
        labelKey = 'label'
    } = props
    const { t } = useTranslation()

    const {
        field,
        error,
        currentInputValue,
        filteredItems,
        pendingValue,
        isValidating,
        handleInputChange,
        handleSelectionChange,
        setIsFocused
    } = useAutocompleteLogic(props)

    const {
        name: _name,
        control: _control,
        rules: _rules,
        options: _options,
        labelKey: _labelKey,
        valueKey: _valueKey,
        onChangeCustom: _onChangeCustom,
        onBeforeChange: _onBeforeChange,
        customRender: _customRender,
        setValue: _setValue,
        listboxProps: _listboxProps,
        showScrollIndicators: _showScrollIndicators,
        scrollShadowProps: _scrollShadowProps,
        onInputChange: _onInputChange,
        disableLocalFilter: _disableLocalFilter,
        disabledTooltip: _disabledTooltip,
        ...restProps
    } = props

    const content = (
        <div className="w-full">
            <Autocomplete
                {...(restProps as any)}
                variant="bordered"
                label={label ? t(label) : undefined}
                placeholder={placeholder ? t(placeholder) : undefined}
                items={filteredItems as any}
                selectedKey={
                    pendingValue !== null
                        ? pendingValue || undefined
                        : field.value || undefined
                }
                inputValue={currentInputValue}
                onInputChange={handleInputChange}
                onSelectionChange={handleSelectionChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                isInvalid={!!error}
                errorMessage={error?.message ? `${error.message}*` : ''}
                classNames={{
                    base: 'w-full',
                    popoverContent: 'w-full',
                    listboxWrapper: 'max-h-[400px] overflow-hidden',
                    listbox: 'overflow-y-auto',
                    ...(props as any)?.classNames
                }}
                listboxProps={{
                    className: 'max-h-60 overflow-y-auto',
                    ...listboxProps
                }}
                showScrollIndicators={showScrollIndicators}
                scrollShadowProps={scrollShadowProps}
                menuTrigger="input"
                allowsCustomValue={false}
                defaultSelectedKey={undefined}
                isDisabled={isValidating || (props.isDisabled ?? false)}
            >
                {(item: OptionsField) => {
                    const itemValue = (item as any)[valueKey] as string
                    const itemLabel = (item as any)[labelKey] as string

                    return (
                        <AutocompleteItem
                            key={itemValue}
                            textValue={itemLabel}
                            classNames={{
                                base: 'data-[hover=true]:bg-default-100 h-auto whitespace-normal',
                                title: 'whitespace-normal'
                            }}
                        >
                            <div className="w-full overflow-hidden">
                                {customRender ? customRender(item) : itemLabel}
                            </div>
                        </AutocompleteItem>
                    )
                }}
            </Autocomplete>
        </div>
    )

    if (props.isDisabled && props.disabledTooltip) {
        return (
            <Tooltip
                content={t(props.disabledTooltip)}
                placement="top"
            >
                {content}
            </Tooltip>
        )
    }

    return content
}
